import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, UploadCloud, FileText, Image, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCreateDocumento, type CreateDocumentoInput } from '@/lib/hooks/documentos';
import { uploadFile } from '@/lib/hooks/pagamentos';
import { cn } from '@/lib/utils';

const ACCEPT_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const ACCEPT_ATTR = ACCEPT_TYPES.join(',');
const MAX_BYTES = 15 * 1024 * 1024;

interface FormValues {
  titulo: string;
  descricao: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULTS: FormValues = { titulo: '', descricao: '' };

function isImageMime(mime: string) {
  return mime.startsWith('image/');
}

export function DocumentoDrawer({ workspaceId, open, onOpenChange }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<{ name: string; r2Key: string; mimeType: string; tamanhoBytes: number } | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const createMut = useCreateDocumento(workspaceId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) {
      reset(DEFAULTS);
      setUploadedFile(null);
      setUploading(false);
      setTagInput('');
      setTags([]);
    }
  }, [open, reset]);

  async function handleFile(file: File) {
    if (!ACCEPT_TYPES.includes(file.type)) {
      toast.error('Formato não suportado. Use PDF, JPG, PNG ou WebP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Arquivo excede 15 MB.');
      return;
    }
    setUploading(true);
    try {
      const key = await uploadFile(file, 'documentos');
      setUploadedFile({ name: file.name, r2Key: key, mimeType: file.type, tamanhoBytes: file.size });
      toast.success('Arquivo enviado.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha no upload.');
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  function removeFile() {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (!t || tags.includes(t) || tags.length >= 20) return;
    if (t.length > 50) {
      toast.error('Tag muito longa (máx. 50 caracteres).');
      return;
    }
    setTags([...tags, t]);
    setTagInput('');
  }

  function removeTag(tag: string) {
    setTags(tags.filter((t) => t !== tag));
  }

  async function onSubmit(values: FormValues) {
    if (!uploadedFile) {
      toast.error('Selecione um arquivo para enviar.');
      return;
    }
    const payload: CreateDocumentoInput = {
      titulo: values.titulo,
      r2Key: uploadedFile.r2Key,
      mimeType: uploadedFile.mimeType,
      tamanhoBytes: uploadedFile.tamanhoBytes,
      tags,
    };
    if (values.descricao.trim()) payload.descricao = values.descricao.trim();
    try {
      await createMut.mutateAsync(payload);
      toast.success('Documento salvo na biblioteca.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar documento.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Biblioteca de documentos"
            title="Adicionar documento"
            description="Suba o arquivo e classifique com tags para facilitar a busca."
          />

          <SheetBody className="space-y-5">
            {/* Dropzone */}
            <div>
              <Label className="mb-2 block">Arquivo</Label>
              {uploadedFile ? (
                <div className="flex items-center gap-3 rounded-2 border border-card-stroke bg-card-soft p-3">
                  <div className="grid h-10 w-10 place-items-center rounded-1 bg-mint-soft text-mint-deep shrink-0">
                    {isImageMime(uploadedFile.mimeType) ? (
                      <Image className="h-4 w-4" strokeWidth={1.8} />
                    ) : (
                      <FileText className="h-4 w-4" strokeWidth={1.8} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-fg truncate">{uploadedFile.name}</p>
                    <p className="text-[11px] text-fg-muted font-mono truncate">{uploadedFile.r2Key}</p>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="grid h-8 w-8 place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card shrink-0"
                    aria-label="Remover arquivo"
                  >
                    <X className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>
              ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'flex flex-col items-center justify-center gap-2 rounded-2 border-2 border-dashed px-4 py-7 cursor-pointer transition-colors',
                    dragOver
                      ? 'border-mint-deep bg-mint-soft/40'
                      : 'border-card-stroke bg-card-soft hover:border-mint-deep/60',
                  )}
                >
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-mint-soft text-mint-deep">
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                    ) : (
                      <UploadCloud className="h-4 w-4" strokeWidth={1.8} />
                    )}
                  </div>
                  <p className="text-sm font-medium text-fg">
                    {uploading ? 'Enviando…' : 'Arraste ou clique para enviar'}
                  </p>
                  <p className="text-[11px] text-fg-muted">PDF, JPG, PNG ou WebP · até 15 MB</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
              />
            </div>

            {/* Título */}
            <div>
              <Label htmlFor="titulo" className="mb-1.5 block">Título</Label>
              <Input
                id="titulo"
                placeholder="Ex.: Nota de empenho março 2026"
                {...register('titulo', {
                  required: 'Obrigatório',
                  minLength: { value: 1, message: 'Obrigatório' },
                  maxLength: { value: 255, message: 'Máximo 255 caracteres' },
                })}
              />
              {errors.titulo && (
                <p className="text-[11px] text-warn mt-1">{errors.titulo.message}</p>
              )}
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="descricao" className="mb-1.5 block">
                Descrição <span className="text-fg-dim font-normal">(opcional)</span>
              </Label>
              <Textarea
                id="descricao"
                placeholder="Contexto adicional sobre o documento…"
                rows={3}
                {...register('descricao', {
                  maxLength: { value: 2000, message: 'Máximo 2000 caracteres' },
                })}
              />
              {errors.descricao && (
                <p className="text-[11px] text-warn mt-1">{errors.descricao.message}</p>
              )}
            </div>

            {/* Tags */}
            <div>
              <Label className="mb-1.5 block">Tags</Label>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-pill bg-mint-soft text-mint-deep border border-transparent px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-wider"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        aria-label={`Remover tag ${tag}`}
                        className="grid place-items-center rounded-full hover:bg-mint-deep/10 h-4 w-4"
                      >
                        <X className="h-2.5 w-2.5" strokeWidth={2.5} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Digite uma tag e pressione Enter…"
                  className="flex-1"
                  maxLength={50}
                />
                <Button type="button" variant="ghost" onClick={addTag} size="sm">
                  Adicionar
                </Button>
              </div>
              <p className="text-[11px] text-fg-dim mt-1">{tags.length}/20 tags</p>
            </div>
          </SheetBody>

          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting || uploading}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Salvar documento'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
